import React, { useEffect, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, SelectChangeEvent } from '@mui/material';
import { useAdminApi } from '../hooks/useAdminApi';
import { OrganizationListResponse } from '../types/adminApiTypes';

interface OrganizationFilterProps {
  value: string[];
  onChange: (selectedOrganizations: string[]) => void;
  label?: string;
  size?: 'small' | 'medium';
}

/**
 * Reusable organization filter component with multi-select checkboxes
 * Fetches available organizations from the /organizations endpoint on mount
 * Empty array = all organizations (no filter applied)
 */
const OrganizationFilter: React.FC<OrganizationFilterProps> = ({
  value,
  onChange,
  label = 'Organizations',
  size = 'small',
}) => {
  const [availableOrganizations, setAvailableOrganizations] = useState<string[]>([]);
  const { data: orgsData, fetchData } = useAdminApi<OrganizationListResponse>();

  // Fetch organizations on mount
  useEffect(() => {
    fetchData({ endpoint: '/organizations' });
  }, [fetchData]);

  // Extract organization names from API response
  useEffect(() => {
    if (orgsData?.organizations) {
      const orgNames = orgsData.organizations.map(org => org.organization);
      setAvailableOrganizations(orgNames);
    }
  }, [orgsData]);

  const isAllSelected = value.length === 0;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const selectedValue = event.target.value;
    const selectedArray = typeof selectedValue === 'string' ? selectedValue.split(',') : selectedValue;

    // Handle "All Organizations" selection
    if (selectedArray.includes('__ALL__')) {
      onChange([]);
    } else {
      onChange(selectedArray);
    }
  };

  // Display label for the selected value(s)
  const getDisplayValue = () => {
    if (value.length === 0) return 'All Organizations';
    if (value.length === 1) return value[0];
    return `${value.length} Organizations`;
  };

  return (
    <FormControl fullWidth size={size}>
      <InputLabel id="organization-filter-label" shrink>
        {label}
      </InputLabel>
      <Select
        labelId="organization-filter-label"
        id="organization-filter"
        multiple
        value={value}
        onChange={handleChange}
        label={label}
        renderValue={getDisplayValue}
        displayEmpty
        notched
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
        }}
      >
        {/* "All Organizations" option */}
        <MenuItem
          value="__ALL__"
          sx={{
            fontWeight: 600,
            borderBottom: '1px solid #e0e0e0',
            py: 1.5,
          }}
        >
          <Checkbox checked={isAllSelected} />
          <ListItemText primary="All Organizations" />
        </MenuItem>

        {/* Individual organization options */}
        {availableOrganizations.map((org) => (
          <MenuItem key={org} value={org}>
            <Checkbox checked={value.includes(org)} />
            <ListItemText primary={org} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default OrganizationFilter;

